import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { iconName, Icon } from "../../../../icons/Icons";
import { amorgLocalStorage } from "../../../../../utils/amorgLocalStorage";

export type MenuItemProps = {
    menuItemKey:string,
    selectedMenuItemKey?:string    
    icon:iconName,
    href:string,
    text:string
}
export function MenuItem(props:MenuItemProps):JSX.Element
{
    return (
        <StyledMenuItem>
            <MenuLinkButton 
                selected={props.selectedMenuItemKey !== undefined && props.menuItemKey === props.selectedMenuItemKey}
                href={props.href}>
                <Icon name={props.icon} />
                <span>{props.text}</span>
            </MenuLinkButton>
        </StyledMenuItem>
    );
}




export type MenuItemGroupProps = {
    menuItemGroupKey:string,
    text:string,
    children?: React.ReactNode
}

export function MenuItemGroup (props: MenuItemGroupProps):JSX.Element
{
    const storageKey = amorgLocalStorage.mainMenu.getKey(props.menuItemGroupKey);
    const [isExpanded, setIsExpanded] = useState(() => {
        const saved = amorgLocalStorage.mainMenu.read(storageKey);
        return saved;
    });

    useEffect(() => {
        amorgLocalStorage.mainMenu.write(storageKey, isExpanded);
    }, [isExpanded, storageKey]);

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggle();
        }
    };              

    return (
        <StyledMenuItem>
            <CollapsibleHeader 
                onClick={handleToggle} 
                onKeyDown={handleKeyDown}
                isExpanded={isExpanded}
                tabIndex={0}
                role="button"
                aria-expanded={isExpanded}
            >
                {props.text}
                <CollapseIcon isExpanded={isExpanded}>
                    <Icon name="arrows_chevron_chevronDown" />
                </CollapseIcon>
            </CollapsibleHeader>
            {isExpanded && <MenuItemGroupItems>{props.children}</MenuItemGroupItems>}
        </StyledMenuItem>
    );
}





function MenuItemGroupItems (props: {children?: React.ReactNode}):JSX.Element
{
    return (
        <MenuItemsContainer>
            <ul>{props.children}</ul>
        </MenuItemsContainer>
    );
}

const StyledMenuItem = styled.li`
    margin-bottom: 0;
`;



const CollapsibleHeader = styled.div<{isExpanded:boolean}>`
    align-items: center;
    border-radius: 4px;
    color: ${props => props.theme.colors.mainNavigation.color};
    cursor: pointer;
    display: flex;
    font-size: 16px;
    font-weight: 400;
    gap: 16px;
    line-height: 26px;
    margin-bottom: 1px;
    padding: 15px;
    position: relative;
    width: 100%;

    div {
        width: 24px;
        height: 24px;
        svg {
            fill: ${props => props.isExpanded ? props.theme.colors.mainNavigation.colorSelected : props.theme.colors.mainNavigation.color};
            width: 24px;
            height: 24px;
        }
    }

    &:hover {
        svg {
            fill: ${props => props.theme.colors.mainNavigation.colorSelected};
        }
    }

    &:focus {
        outline: 1px solid ${props => props.theme.colors.mainNavigation.colorSelected};
        outline-offset: 1px;
    }
`;

const CollapseIcon = styled.div<{isExpanded:boolean}>`
    margin-left: auto;
    transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.2s ease;
`;

const MenuItemsContainer = styled.div`
    padding-left: 16px;
    margin-top: 4px;
    
    ul {
        list-style: none;
        margin: 0;
        padding: 0;
    }
`;

const MenuLinkButton = styled.a<{selected:boolean}>`
    
    align-items: center;
    background-color:${props => props.selected ? props.theme.colors.mainNavigation.backgroundSelected : props.theme.colors.mainNavigation.background};
    border-radius: 4px;
    color: ${props => props.selected ? props.theme.colors.mainNavigation.colorSelected : props.theme.colors.mainNavigation.color};
    display: flex;
    font-size:16px;
    font-weight: 400;
    gap:16px;
    line-height: 26px;
    margin-bottom: 1px;
    padding:15px;
    text-decoration: none;
    width:100%;
    box-sizing: border-box;
    
    div {
        width: 24px;
        height:24px;
        svg {
            fill:${props => props.selected ? props.theme.colors.mainNavigation.colorSelected : props.theme.colors.mainNavigation.color};
            width: 24px;
            height:24px;
        }
    }
    
    &:hover{
        background-color: ${props => props.theme.colors.mainNavigation.backgroundSelected};
        color:${props => props.theme.colors.mainNavigation.colorSelected};

        svg {
            fill:${props => props.theme.colors.mainNavigation.colorSelected};
        }
    }
`;